import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import type {
    StylesConfig,
    Props,
    OnChangeValue,
    GroupBase,
} from 'react-select';
import Select from 'react-select';
import type { CreatableProps } from 'react-select/creatable';
import CreatableSelect from 'react-select/creatable';

import { SelectContainer, StyledLabel } from './styled';

import { theme } from '../../theme/config';
import type { IOption } from '../../types/option';
import { getBorderShadow, StyledError } from '../FormInput/styled';

interface IFormSelectOption extends IOption {
    disabled?: boolean;
}
type IFormSelect<TCreatable extends boolean> = ComponentProps<
    typeof SelectContainer
> & {
    id: string;
    label: string;
    options: IFormSelectOption[];
    placeholder?: string;

    /**
     * Overrides select props
     */
    selectProps?: Omit<
        TCreatable extends false
            ? Props<IFormSelectOption>
            : CreatableProps<
                  IFormSelectOption,
                  boolean,
                  GroupBase<IFormSelectOption>
              >,
        'options' | 'onChange'
    >;

    isCreatable?: TCreatable;
};

/**
 * In order to work properly, this component needs to be in some FormProvider
 */
export function FormSelect<TCreatable extends boolean>({
    id,
    label,
    options,
    placeholder,
    selectProps,
    isCreatable,
    onSelectOption,
    ...containerProps
}: IFormSelect<TCreatable>) {
    const { clearErrors } = useFormContext();
    const {
        field: { onBlur, onChange, value, name, ref },
        fieldState: { error },
    } = useController({
        name: id,
    });

    const actualValue = useMemo(
        () => options.find((option) => option.value === value),
        [value, options]
    );

    const styles = useMemo(
        (): StylesConfig<IFormSelectOption> => ({
            indicatorSeparator: (provided, state) => ({
                ...provided,
                display: 'none',
                ...selectProps?.styles?.indicatorSeparator?.(provided, state),
            }),
            container: (provided, state) => ({
                ...provided,
                color: theme.colors.formLabel,
                fontSize: theme.fontSizes[3],
                ...selectProps?.styles?.container?.(provided, state),
            }),
            control: (provided, state) => ({
                ...provided,
                height: '2.25rem',
                minHeight: '2.25rem',
                borderRadius: '0.375rem',
                border: 'none',
                boxShadow: getBorderShadow(Boolean(error), state.isFocused),
                '&:hover, &:focus': {
                    outline: 'none',
                    boxShadow: getBorderShadow(Boolean(error), state.isFocused),
                },
                ...selectProps?.styles?.control?.(provided, state),
            }),
            input: (provided, state) => ({
                ...provided,
                height: 'auto',
                ...selectProps?.styles?.input?.(provided, state),
            }),
        }),
        [error, selectProps?.styles]
    );

    const { ...selectPropsCopy } = selectProps ?? { styles: undefined };
    selectPropsCopy.styles = styles;

    const onChangeWrapper = useCallback(
        (value: OnChangeValue<IFormSelectOption, boolean>) => {
            if (onSelectOption) {
                onSelectOption(value);
            }
            if (!value) {
                onChange(undefined);
                return;
            }
            if ('value' in value) {
                onChange(value.value);
            } else {
                onChange(value.map((option) => option.value));
            }
        },
        [onChange, clearErrors, id]
    );

    const SelectComponent = isCreatable ? CreatableSelect : Select;

    return (
        <SelectContainer {...containerProps} hasError={Boolean(error)}>
            <StyledLabel htmlFor={id}>{label}</StyledLabel>
            <SelectComponent
                {...selectPropsCopy}
                options={options}
                onChange={onChangeWrapper}
                onBlur={onBlur}
                value={actualValue}
                name={name}
                ref={ref}
                placeholder={placeholder}
            />
            <StyledError>{error?.message}</StyledError>
        </SelectContainer>
    );
}

FormSelect.defaultProps = {
    placeholder: undefined,
    selectProps: undefined,
    isCreatable: false,
};
